import { useState, useEffect, useContext } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { AuthContext } from "@/App";
import { toast } from "sonner";
import { Send, MessageCircle, Shield, AlertTriangle } from "lucide-react";
import { useLocation } from "react-router-dom";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { VerifiedBadge } from "@/components/VerifiedBadge";

export default function Messages() {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMessages();
    
    // If navigated from listing detail with receiver info
    if (location.state?.receiverId) {
      setSelectedUser({
        id: location.state.receiverId,
        name: location.state.receiverName
      });
    }
  }, []);

  useEffect(() => {
    if (selectedUser) {
      fetchConversation(selectedUser.id, selectedUser.listing_id);
    }
  }, [selectedUser]);

  const fetchMessages = async () => {
    try {
      const { data } = await api.get("/messages");
      setMessages(data);
      extractConversations(data);
    } catch (error) {
      toast.error("Failed to fetch messages");
    } finally {
      setLoading(false);
    }
  };

  const extractConversations = async (allMessages) => {
    const conversationMap = new Map();
    
    allMessages.forEach(msg => {
      const otherUserId = msg.sender_id === user.id ? msg.receiver_id : msg.sender_id;
      const otherUserName = msg.sender_id === user.id ? msg.receiver_name : msg.sender_name;
      const otherUserVerified = msg.sender_id === user.id ? msg.receiver_verified : msg.sender_verified;
      
      // Create unique key: user + listing (so same users can have multiple conversations for different listings)
      const conversationKey = `${otherUserId}_${msg.listing_id || 'general'}`;
      
      if (!conversationMap.has(conversationKey)) {
        conversationMap.set(conversationKey, {
          id: otherUserId,
          name: otherUserName,
          lastMessage: msg.content,
          timestamp: msg.created_at,
          listing_id: msg.listing_id,
          conversationKey: conversationKey,
          verified: otherUserVerified || false
        });
      }
    });
    
    const conversationList = Array.from(conversationMap.values());
    
    // Fetch listing names for conversations
    for (const conv of conversationList) {
      if (conv.listing_id) {
        try {
          const { data } = await api.get(`/listings/${conv.listing_id}`);
          conv.listing_name = data.name;
        } catch (error) {
          conv.listing_name = "Unknown Listing";
        }
      }
    }
    
    setConversations(conversationList);
  };

  const fetchConversation = async (userId, listingId = null) => {
    try {
      const { data } = await api.get(`/conversations/${userId}`);
      // Filter messages for this specific listing if listingId is provided
      const filteredMessages = listingId 
        ? data.filter(msg => msg.listing_id === listingId)
        : data.filter(msg => !msg.listing_id); // General messages without listing
      setMessages(filteredMessages);
    } catch (error) {
      toast.error("Failed to fetch conversation");
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUser) return;

    try {
      const messagePayload = {
        receiver_id: selectedUser.id,
        content: newMessage
      };
      
      // Include listing_id if this conversation is about a specific listing
      if (selectedUser.listing_id) {
        messagePayload.listing_id = selectedUser.listing_id;
      }
      
      await api.post("/messages", messagePayload);
      setNewMessage("");
      fetchConversation(selectedUser.id, selectedUser.listing_id);
    } catch (error) {
      // Handle contact sharing blocked error
      if (error.response?.status === 400 && error.response?.data?.detail?.includes("Contact sharing")) {
        toast.error(error.response.data.detail, {
          duration: 6000,
          icon: <AlertTriangle className="w-5 h-5 text-amber-500" />
        });
      } else {
        toast.error("Failed to send message");
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-12">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-24">
        <h1 className="text-4xl font-bold text-slate-900 mb-8" data-testid="messages-header">Messages</h1>

        <div className="grid md:grid-cols-3 gap-6 h-[600px]">
          {/* Conversations List */}
          <Card className="md:col-span-1" data-testid="conversations-list">
            <CardHeader>
              <CardTitle>Conversations</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[500px]">
                {conversations.length === 0 ? (
                  <div className="p-4 text-center text-slate-500">
                    <MessageCircle className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">No conversations yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {conversations.map((conv) => (
                      <div
                        key={conv.conversationKey}
                        onClick={() => setSelectedUser(conv)}
                        className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                          selectedUser?.conversationKey === conv.conversationKey ? "bg-blue-50 border-l-4 border-blue-600" : ""
                        }`}
                        data-testid={`conversation-${conv.conversationKey}`}
                      >
                        <div className="flex items-center gap-1.5">
                          <h3 className="font-semibold text-slate-900">{conv.name}</h3>
                          {conv.verified && <VerifiedBadge size="xs" />}
                        </div>
                        {conv.listing_name && (
                          <p className="text-xs text-blue-600 font-medium">Re: {conv.listing_name}</p>
                        )}
                        <p className="text-sm text-slate-500 truncate mt-1">{conv.lastMessage}</p>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Messages */}
          <Card className="md:col-span-2" data-testid="messages-panel">
            {selectedUser ? (
              <>
                <CardHeader className="border-b border-slate-200 pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle>{selectedUser.name}</CardTitle>
                  </div>
                  {/* Monitoring Badge */}
                  <div className="flex items-center gap-2 mt-2 px-3 py-2 bg-slate-100 rounded-lg">
                    <Shield className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs text-slate-600">
                      ReBusi Deal Advisor is monitoring this conversation for your safety
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="p-0 flex flex-col h-[460px]">
                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-4">
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.is_admin_message ? "justify-center" : msg.sender_id === user.id ? "justify-end" : "justify-start"}`}
                        >
                          {msg.is_admin_message ? (
                            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 max-w-[85%]">
                              <div className="flex items-center gap-2 mb-1">
                                <Shield className="w-4 h-4 text-emerald-600" />
                                <span className="text-xs font-semibold text-emerald-700">ReBusi Team</span>
                              </div>
                              <p className="text-sm text-slate-700">{msg.content}</p>
                              <p className="text-xs text-slate-500 mt-1">
                                {new Date(msg.created_at).toLocaleTimeString()}
                              </p>
                            </div>
                          ) : (
                            <div
                              className={`max-w-[70%] rounded-lg p-3 ${
                                msg.sender_id === user.id
                                  ? "bg-blue-600 text-white"
                                  : "bg-slate-100 text-slate-900"
                              }`}
                            >
                              {/* Show sender name with verified badge for received messages */}
                              {msg.sender_id !== user.id && (
                                <div className="flex items-center gap-1.5 mb-1">
                                  <span className="text-xs font-semibold text-slate-700">{msg.sender_name}</span>
                                  {msg.sender_verified && <VerifiedBadge size="xs" />}
                                </div>
                              )}
                              <p className="text-sm">{msg.content}</p>
                              <p
                                className={`text-xs mt-1 ${
                                  msg.sender_id === user.id ? "text-blue-100" : "text-slate-500"
                                }`}
                              >
                                {new Date(msg.created_at).toLocaleTimeString()}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                  
                  <form onSubmit={handleSendMessage} className="p-4 border-t border-slate-200">
                    <div className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message..."
                        data-testid="message-input"
                      />
                      <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="send-message-button">
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-slate-500">
                  <MessageCircle className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <p>Select a conversation to start messaging</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}